// Potion and Consumable System

export type PotionType =
  | 'hp_small' | 'hp_medium' | 'hp_large'
  | 'mana_small' | 'mana_medium' | 'mana_large'
  | 'regen' | 'speed' | 'power' | 'resistance'
  | 'antidote' | 'stone_skin';

export interface PotionDefinition {
  id: PotionType;
  name: string;
  description: string;
  color: string;          // Bottle color for rendering
  effect: PotionEffect;
  cooldownGroup: string;  // Cooldown group (hp, mana, utility)
  cooldown: number;       // Seconds
  maxStack: number;
  buyPrice: number;
  sellPrice: number;
  levelRequired: number;
}

export interface PotionEffect {
  type: 'instant_heal' | 'instant_mana' | 'buff';
  value: number;         // Amount or percentage
  isPercent: boolean;
  duration?: number;     // For buffs, seconds
  buffStat?: string;     // Which stat to buff
  buffValue?: number;    // Buff amount
  cleanse?: string[];    // Status effects to remove
}

export const POTION_DEFINITIONS: Record<PotionType, PotionDefinition> = {
  hp_small: {
    id: 'hp_small',
    name: 'Kucuk Can Iksiri',
    description: 'Aninda %30 HP yeniler.',
    color: '#CC2222',
    effect: { type: 'instant_heal', value: 30, isPercent: true },
    cooldownGroup: 'hp',
    cooldown: 3,
    maxStack: 20,
    buyPrice: 50,
    sellPrice: 15,
    levelRequired: 1,
  },
  hp_medium: {
    id: 'hp_medium',
    name: 'Orta Can Iksiri',
    description: 'Aninda %50 HP yeniler.',
    color: '#DD3333',
    effect: { type: 'instant_heal', value: 50, isPercent: true },
    cooldownGroup: 'hp',
    cooldown: 3,
    maxStack: 20,
    buyPrice: 150,
    sellPrice: 45,
    levelRequired: 10,
  },
  hp_large: {
    id: 'hp_large',
    name: 'Buyuk Can Iksiri',
    description: 'Aninda %80 HP yeniler.',
    color: '#EE4444',
    effect: { type: 'instant_heal', value: 80, isPercent: true },
    cooldownGroup: 'hp',
    cooldown: 3,
    maxStack: 20,
    buyPrice: 400,
    sellPrice: 120,
    levelRequired: 25,
  },
  mana_small: {
    id: 'mana_small',
    name: 'Kucuk Mana Iksiri',
    description: 'Aninda %30 Mana yeniler.',
    color: '#2244CC',
    effect: { type: 'instant_mana', value: 30, isPercent: true },
    cooldownGroup: 'mana',
    cooldown: 3,
    maxStack: 20,
    buyPrice: 50,
    sellPrice: 15,
    levelRequired: 1,
  },
  mana_medium: {
    id: 'mana_medium',
    name: 'Orta Mana Iksiri',
    description: 'Aninda %50 Mana yeniler.',
    color: '#3355DD',
    effect: { type: 'instant_mana', value: 50, isPercent: true },
    cooldownGroup: 'mana',
    cooldown: 3,
    maxStack: 20,
    buyPrice: 150,
    sellPrice: 45,
    levelRequired: 10,
  },
  mana_large: {
    id: 'mana_large',
    name: 'Buyuk Mana Iksiri',
    description: 'Aninda %80 Mana yeniler.',
    color: '#4466EE',
    effect: { type: 'instant_mana', value: 80, isPercent: true },
    cooldownGroup: 'mana',
    cooldown: 3,
    maxStack: 20,
    buyPrice: 400,
    sellPrice: 120,
    levelRequired: 25,
  },
  regen: {
    id: 'regen',
    name: 'Canlandirma Iksiri',
    description: '10 saniye boyunca saniyede %3 HP yeniler.',
    color: '#22AA44',
    effect: { type: 'buff', value: 3, isPercent: true, duration: 10, buffStat: 'hpRegen', buffValue: 3 },
    cooldownGroup: 'utility',
    cooldown: 30,
    maxStack: 10,
    buyPrice: 200,
    sellPrice: 60,
    levelRequired: 5,
  },
  speed: {
    id: 'speed',
    name: 'Hiz Iksiri',
    description: '8 saniye boyunca %30 hareket hizi artisi.',
    color: '#44CC88',
    effect: { type: 'buff', value: 30, isPercent: true, duration: 8, buffStat: 'movementSpeed', buffValue: 30 },
    cooldownGroup: 'utility',
    cooldown: 30,
    maxStack: 10,
    buyPrice: 200,
    sellPrice: 60,
    levelRequired: 5,
  },
  power: {
    id: 'power',
    name: 'Guc Iksiri',
    description: '10 saniye boyunca %20 hasar artisi.',
    color: '#CC8822',
    effect: { type: 'buff', value: 20, isPercent: true, duration: 10, buffStat: 'damage', buffValue: 20 },
    cooldownGroup: 'utility',
    cooldown: 30,
    maxStack: 10,
    buyPrice: 250,
    sellPrice: 75,
    levelRequired: 8,
  },
  resistance: {
    id: 'resistance',
    name: 'Direnc Iksiri',
    description: '10 saniye boyunca +20 tum direncler.',
    color: '#8844CC',
    effect: { type: 'buff', value: 20, isPercent: false, duration: 10, buffStat: 'allResistance', buffValue: 20 },
    cooldownGroup: 'utility',
    cooldown: 30,
    maxStack: 10,
    buyPrice: 250,
    sellPrice: 75,
    levelRequired: 8,
  },
  antidote: {
    id: 'antidote',
    name: 'Panzehir',
    description: 'Zehir ve kanama efektlerini temizler.',
    color: '#88DD44',
    effect: { type: 'instant_heal', value: 0, isPercent: false, cleanse: ['poison', 'bleed'] },
    cooldownGroup: 'utility',
    cooldown: 10,
    maxStack: 10,
    buyPrice: 100,
    sellPrice: 30,
    levelRequired: 3,
  },
  stone_skin: {
    id: 'stone_skin',
    name: 'Tas Derisi Iksiri',
    description: '5 saniye boyunca %50 hasar azaltma.',
    color: '#888888',
    effect: { type: 'buff', value: 50, isPercent: true, duration: 5, buffStat: 'damageReduction', buffValue: 50 },
    cooldownGroup: 'utility',
    cooldown: 60,
    maxStack: 5,
    buyPrice: 500,
    sellPrice: 150,
    levelRequired: 15,
  },
};

export interface PotionSlot {
  potionType: PotionType;
  count: number;
  maxCount: number;
}

// Potion belt - manages equipped potions and cooldowns
export class PotionBelt {
  // 4 potion slots (Q, E, and 2 more for belt expansion)
  public slots: (PotionSlot | null)[] = [null, null, null, null];
  private cooldowns: Map<string, number> = new Map(); // cooldownGroup -> remaining seconds

  // Max available slots (2 base, belt item can increase to 4)
  public maxSlots: number = 2;

  assignPotion(slotIndex: number, potionType: PotionType, count: number): void {
    if (slotIndex < 0 || slotIndex >= this.maxSlots) return;
    const def = POTION_DEFINITIONS[potionType];
    if (!def) return;
    this.slots[slotIndex] = { potionType, count, maxCount: def.maxStack };
  }

  usePotion(slotIndex: number): PotionEffect | null {
    if (slotIndex < 0 || slotIndex >= this.maxSlots) return null;
    const slot = this.slots[slotIndex];
    if (!slot || slot.count <= 0) return null;

    const def = POTION_DEFINITIONS[slot.potionType];
    if (!def) return null;

    // Check cooldown
    const remaining = this.cooldowns.get(def.cooldownGroup) || 0;
    if (remaining > 0) return null;

    // Use potion
    slot.count--;
    this.cooldowns.set(def.cooldownGroup, def.cooldown);

    // Remove slot if empty
    if (slot.count <= 0) {
      this.slots[slotIndex] = null;
    }

    return def.effect;
  }

  update(deltaTime: number): void {
    for (const [group, remaining] of this.cooldowns) {
      const newRemaining = remaining - deltaTime;
      if (newRemaining <= 0) {
        this.cooldowns.delete(group);
      } else {
        this.cooldowns.set(group, newRemaining);
      }
    }
  }

  getCooldown(slotIndex: number): number {
    const slot = this.slots[slotIndex];
    if (!slot) return 0;
    const def = POTION_DEFINITIONS[slot.potionType];
    if (!def) return 0;
    return this.cooldowns.get(def.cooldownGroup) || 0;
  }

  getCooldownMax(slotIndex: number): number {
    const slot = this.slots[slotIndex];
    if (!slot) return 0;
    const def = POTION_DEFINITIONS[slot.potionType];
    return def ? def.cooldown : 0;
  }

  /**
   * Serialize for saving
   */
  serialize(): object {
    return {
      slots: this.slots.map(s => s ? { potionType: s.potionType, count: s.count } : null),
      maxSlots: this.maxSlots,
    };
  }

  /**
   * Deserialize from save
   */
  deserialize(data: { slots?: ({ potionType: PotionType; count: number } | null)[]; maxSlots?: number }): void {
    if (data.maxSlots !== undefined) {
      this.maxSlots = data.maxSlots;
    }
    if (data.slots) {
      for (let i = 0; i < data.slots.length && i < this.slots.length; i++) {
        const saved = data.slots[i];
        if (saved) {
          const def = POTION_DEFINITIONS[saved.potionType];
          if (def) {
            this.slots[i] = { potionType: saved.potionType, count: saved.count, maxCount: def.maxStack };
          }
        } else {
          this.slots[i] = null;
        }
      }
    }
  }
}

// Get all potions available at a vendor by level
export function getAvailablePotions(playerLevel: number): PotionDefinition[] {
  return Object.values(POTION_DEFINITIONS).filter(p => p.levelRequired <= playerLevel);
}
